import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import { PaymentClient, CampaignClient } from '../../../services/apiClient';
import axiosInstance from '../../../services/axiosInstance';
import Loader from '../../../components/common/Loader';
import SimpleAlert from '../../../components/common/SimpleAlert';
import Button from '../../../components/common/Button';

const PaymentReturnPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();

  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const paymentClient = new PaymentClient(API_BASE_URL, axiosInstance);
  const campaignClient = new CampaignClient(API_BASE_URL, axiosInstance);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!paymentId) return;

      try {
        const paymentRes = await paymentClient.getPaymentById(paymentId);
        const payment = paymentRes?.data;

        if (!payment) {
          setPaymentDetails(null);
          return;
        }

        setPaymentDetails(payment);

        const imageRes = await campaignClient.generateCampaignImageSas(
          payment.campaignId ?? '',
        );
        if (imageRes?.success) setBackgroundImage(imageRes.data ?? '');
      } catch (err) {
        console.error('Failed to load payment return data', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [paymentId]);

  const isSuccess = paymentDetails?.paymentStatusName === 'Success';
  const isPending = paymentDetails?.paymentStatusName === 'Pending';
  const isFailed = !isSuccess && !isPending;

  return (
    <div
      className="w-full h-screen flex items-center justify-center relative"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50 z-0" />
      <div className="z-10 bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-center">
        <img src="/images/logo.png" className="mx-auto mb-6 w-40" />

        {isLoading ? (
          <>
            <div className="text-gray-700 text-lg font-semibold">
              Finalizing payment...
            </div>
            <Loader />
          </>
        ) : paymentDetails ? (
          <>
            <div
              className={`text-xl font-semibold mb-2 ${
                isSuccess
                  ? 'text-green-600'
                  : isPending
                    ? 'text-yellow-600'
                    : isFailed
                      ? 'text-red-600'
                      : ''
              }`}
            >
              Payment Status: {paymentDetails.paymentStatusName}
            </div>

            <div className="text-gray-700 text-base mb-4">
              <p>
                <strong>Donor:</strong> {paymentDetails.memberName}
              </p>
              <p>
                <strong>Email:</strong> {paymentDetails.memberEmail}
              </p>
              <p>
                <strong>Campaign:</strong> {paymentDetails.campaignName}
              </p>
              <p>
                <strong>Amount:</strong> R
                {Number(paymentDetails.amount ?? 0).toFixed(2)}
              </p>
              <p>
                <strong>Reference:</strong> {paymentDetails.id}
              </p>
            </div>

            {isSuccess && (
              <SimpleAlert
                variant="success"
                message="Thank you for your generous donation! A confirmation email will be sent to you shortly."
              />
            )}

            {isPending && (
              <SimpleAlert
                variant="info"
                message="Your payment is being processed. We'll notify you once it's confirmed."
              />
            )}

            {isFailed && (
              <>
                <SimpleAlert
                  variant="error"
                  message="Unfortunately, your payment did not go through."
                />
                <Button type='button'
                  onClick={() =>
                    navigate(`/campaign-donation/redirect/${paymentDetails.id}`)
                  }
                >
                  Try Again
                </Button>
              </>
            )}
          </>
        ) : (
          <SimpleAlert
            variant="error"
            message="We couldn’t retrieve your payment details. Please contact support."
          />
        )}
      </div>
    </div>
  );
};

export default PaymentReturnPage;
